import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link as MuiLink,
  Typography
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  BoltOutlined as BoltIcon,
  CheckCircle as CheckCircleIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomerLayout from '../../components/customer/CustomerLayout';
// Ayuda de ESTA pantalla, con el mismo sistema que «Mi cuenta»: el panel corto
// resuelve la duda del momento y su «saber más» lleva a la guía global.
import AccountHelpPanel from '../../components/customer/AccountHelpPanel';
import customerApiService from '../../services/customerApi';
import { CreditPackage, CustomerPackages } from '../../types/creditPackage';

// Catálogo de PAQUETES DE CRÉDITOS del producto «empleados IA».
//
// Esta página enseñaba antes la tabla `plans`: las suscripciones B2C de TENANTS
// por Paddle, con IDs de precio de relleno y textos como «Up to 5 tenants». Ese
// es otro producto (y sigue vivo para quien lo tenga contratado); lo que un
// cliente de empleados IA compra son CRÉDITOS, y son de dos clases:
//
//   · PLAN mensual  → se renueva cada ciclo y sus créditos CADUCAN al cerrarlo.
//     Más barato por crédito cuanto más grande, y el ahorro se pinta.
//   · Pack EXTRA    → puntual y NO caduca. Algo más caro por crédito a
//     propósito: no caducar es un servicio, y así el plan siempre sale a cuenta.
//
// PASARELA: no la hay todavía. Al confirmar NO se cobra nada — el backend
// registra la intención y devuelve `pending_gateway`, y aquí se dice sin
// rodeos. El enganche de Paddle está señalado en el endpoint del backend
// (customer_account.purchase_package).
//
// Las cuentas GESTIONADAS POR UN PARTNER no llegan aquí: la ruta va tras
// ManagedAccountGuard (App.tsx) y el backend además corta la compra con un 403.
// Su interlocutor es su partner, no este portal.

const formatCredits = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toLocaleString();
};

const formatUsd = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toFixed(2);
};

const formatPerCredit = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toFixed(3);
};

// Ahorro (%) de un paquete frente al MÁS CARO por crédito de su familia — que
// es siempre el más pequeño. Se pinta solo si es apreciable (≥ 1 %): un chip
// de «ahorras 0 %» es ruido.
const savingsPct = (pkg: CreditPackage, family: CreditPackage[]): number => {
  const rates = family
    .map((p) => Number(p.price_per_credit))
    .filter((n) => !Number.isNaN(n) && n > 0);
  if (rates.length === 0) return 0;
  const worst = Math.max(...rates);
  const own = Number(pkg.price_per_credit);
  if (!worst || !own || Number.isNaN(own)) return 0;
  return Math.round(((worst - own) / worst) * 100);
};

const CustomerPlansPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<CustomerPackages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cuenta inexistente (el EndUser aún no tiene cuenta de empleados IA): no es
  // un error, es un estado — y merece su propio mensaje, no un rojo.
  const [noAccount, setNoAccount] = useState(false);
  const [selected, setSelected] = useState<CreditPackage | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Resultado de la confirmación: hoy SIEMPRE «pendiente de pasarela».
  const [pending, setPending] = useState<{
    pkg: CreditPackage;
    contactName?: string | null;
    contactEmail?: string | null;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customerApiService.getCreditPackages();
      setData(response);
      setNoAccount(false);
    } catch (err: any) {
      if (err?.status === 404 || err?.detail?.includes?.('no tiene una cuenta')) {
        setNoAccount(true);
      } else {
        console.error('Failed to load credit packages:', err);
        setError(t('customer.packages.loadError', 'No se pudo cargar el catálogo de paquetes'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirm = async () => {
    if (!selected) return;
    try {
      setConfirming(true);
      const res = await customerApiService.purchaseCreditPackage(selected.id);
      // No se pinta «comprado» jamás: el backend devuelve un ESTADO explícito
      // y hoy solo puede ser «pendiente de pasarela».
      setPending({
        pkg: res.package ?? selected,
        contactName: res.contact_name,
        contactEmail: res.contact_email
      });
      setSelected(null);
    } catch (err: any) {
      console.error('Failed to register purchase intent:', err);
      setError(err?.detail || t('customer.packages.purchaseError', 'No se pudo registrar la solicitud'));
      setSelected(null);
    } finally {
      setConfirming(false);
    }
  };

  const renderPackage = (
    pkg: CreditPackage,
    family: CreditPackage[],
    isCurrent: boolean
  ) => {
    const saving = savingsPct(pkg, family);
    return (
      <Grid item xs={12} sm={6} md={3} key={pkg.id}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            border: '1px solid',
            borderColor: isCurrent ? 'primary.main' : '#e0e0e0',
            bgcolor: isCurrent ? '#f4f9ff' : 'background.paper'
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {pkg.name}
              </Typography>
              {isCurrent && (
                <Chip
                  size="small"
                  color="primary"
                  icon={<CheckCircleIcon />}
                  label={t('customer.packages.current', 'Contratado')}
                />
              )}
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {formatCredits(pkg.credits)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('customer.packages.creditsUnit', 'créditos')}
            </Typography>
            <Box mt={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formatUsd(pkg.price_usd)} USD
                {pkg.kind === 'plan' && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}
                    {t('customer.packages.perMonth', '/mes')}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('customer.packages.perCredit', '{{price}} USD por crédito', {
                  price: formatPerCredit(pkg.price_per_credit)
                })}
              </Typography>
            </Box>
            {saving >= 1 && (
              <Chip
                size="small"
                variant="outlined"
                color="success"
                sx={{ mt: 1 }}
                label={t('customer.packages.savings', 'Ahorras un {{pct}} %', { pct: saving })}
              />
            )}
            {/* PARA QUÉ VALE: el texto que hace comprensible el paquete. Lo
                escribe el administrador (columna what_for) — no se calcula
                aquí, porque la equivalencia depende del empleado y del uso. */}
            {pkg.what_for && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {pkg.what_for}
              </Typography>
            )}
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2 }}>
            <Button
              fullWidth
              variant={isCurrent ? 'outlined' : 'contained'}
              disabled={isCurrent}
              onClick={() => setSelected(pkg)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none' }}
            >
              {isCurrent
                ? t('customer.packages.currentAction', 'Es tu plan actual')
                : pkg.kind === 'plan'
                  ? t('customer.packages.choosePlan', 'Elegir este plan')
                  : t('customer.packages.buyExtra', 'Comprar')}
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <CustomerLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </CustomerLayout>
    );
  }

  if (noAccount) {
    return (
      <CustomerLayout>
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, textAlign: 'center', py: 6, px: 3 }}>
            <AutoAwesomeIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('customer.packages.noAccountTitle', 'Todavía no tienes una cuenta de empleados IA')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('customer.packages.noAccountMessage', 'Cuando tengas tu cuenta activa podrás contratar aquí tus paquetes de créditos. Si crees que deberías tenerla, contacta con tu proveedor.')}
            </Typography>
          </Card>
        </Container>
      </CustomerLayout>
    );
  }

  const plans = data?.plans ?? [];
  const extras = data?.extras ?? [];
  const currentId = data?.current_plan_package_id ?? null;
  const currentCredits = Number(data?.current_plan_credits ?? 0);
  // Plan A MEDIDA: la cuenta tiene créditos de plan pero no coinciden con
  // ningún paquete del catálogo. Decirlo es mejor que marcar la tarjeta que
  // más se le parece — el cliente creería que puede compararla.
  const customPlan = currentCredits > 0 && currentId === null;

  return (
    <CustomerLayout>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box mb={4}>
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 1 }}>
            {t('customer.packages.title', 'Paquetes de créditos')}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary">
            {t('customer.packages.subtitle', 'Tus empleados IA trabajan con créditos. Elige el plan que encaje con tu ritmo y añade packs extra cuando tengas un pico.')}
          </Typography>
          <Box display="flex" justifyContent="center" mt={1}>
            <AccountHelpPanel
              blocks={['packages']}
              label={t('customer.packages.help', '¿Cómo funcionan los créditos?')}
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {customPlan && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('customer.packages.customPlan', 'Tienes un plan a medida de {{credits}} créditos al mes. Si cambias a uno de estos paquetes, sustituirá al actual.', {
              credits: formatCredits(currentCredits)
            })}
          </Alert>
        )}

        {/* ── Planes mensuales ────────────────────────────────────────── */}
        <Box mb={2}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('customer.packages.plansTitle', 'Plan mensual')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('customer.packages.plansHelp', 'Se renueva cada mes. Los créditos del plan se usan primero y caducan al cerrar el ciclo: elige el tamaño que de verdad vayas a gastar.')}
          </Typography>
        </Box>
        {plans.length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            {t('customer.packages.noPlans', 'Ahora mismo no hay planes publicados. Contacta con tu proveedor.')}
          </Alert>
        ) : (
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {plans.map((pkg) => renderPackage(pkg, plans, pkg.id === currentId))}
          </Grid>
        )}

        <Divider sx={{ mb: 4 }} />

        {/* ── Packs extra ─────────────────────────────────────────────── */}
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <BoltIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('customer.packages.extrasTitle', 'Comprar créditos extra')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('customer.packages.extrasHelp', 'Compra puntual para un pico de trabajo. Estos créditos NO caducan: se quedan en tu saldo hasta que los uses, y por eso salen algo más caros por crédito que el plan.')}
          </Typography>
        </Box>
        {extras.length === 0 ? (
          <Alert severity="info">
            {t('customer.packages.noExtras', 'Ahora mismo no hay packs extra publicados.')}
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {extras.map((pkg) => renderPackage(pkg, extras, false))}
          </Grid>
        )}

        {/* Cómo se gastan los créditos: la letra que evita la pregunta de
            soporte «¿por qué me han desaparecido créditos?». */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 4 }}>
          <AlertTitle>{t('customer.packages.howTitle', 'Cómo se gastan tus créditos')}</AlertTitle>
          {t('customer.packages.howBody', 'Primero se consumen los créditos del plan del mes y después los extra. Lo que sobra del plan caduca al cerrar el ciclo; los extra se quedan. En «Mi cuenta» ves tu saldo, tu consumo y una estimación de hasta cuándo te llega.')}
          <Box mt={1}>
            <Button size="small" onClick={() => navigate('/customer/account')} sx={{ textTransform: 'none' }}>
              {t('customer.packages.goToAccount', 'Ir a Mi cuenta')}
            </Button>
          </Box>
        </Alert>
      </Container>

      {/* Confirmación. El aviso de que NO se cobra va DENTRO del diálogo, junto
          al botón: es el último momento en que se puede evitar el malentendido. */}
      <Dialog open={!!selected} onClose={() => !confirming && setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selected?.kind === 'plan'
            ? t('customer.packages.confirmPlanTitle', 'Contratar el plan {{name}}', { name: selected?.name ?? '' })
            : t('customer.packages.confirmExtraTitle', 'Comprar el pack {{name}}', { name: selected?.name ?? '' })}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1">
                {t('customer.packages.confirmSummary', '{{credits}} créditos por {{price}} USD', {
                  credits: formatCredits(selected.credits),
                  price: formatUsd(selected.price_usd)
                })}
                {selected.kind === 'plan' ? ` ${t('customer.packages.perMonth', '/mes')}` : ''}
              </Typography>
              {selected.what_for && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selected.what_for}
                </Typography>
              )}
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>{t('customer.packages.noChargeTitle', 'Todavía no se cobra')}</AlertTitle>
                {t('customer.packages.noChargeBody', 'El pago con tarjeta estará disponible en breve. Al confirmar dejamos registrada tu solicitud, pero no se te cobrará nada ni se añadirán créditos todavía. Si los necesitas ya, pídeselos a tu proveedor.')}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setSelected(null)} disabled={confirming} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={confirming}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {confirming
              ? <CircularProgress size={20} />
              : t('customer.packages.confirmAction', 'Solicitar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resultado: solicitud registrada, NADA cobrado. */}
      <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('customer.packages.pendingTitle', 'Solicitud registrada')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            {t('customer.packages.pendingBody', 'Hemos anotado tu interés en {{name}} ({{credits}} créditos). El pago online estará disponible en breve; mientras tanto, puedes pedírselo directamente a tu proveedor y te lo activará al momento.', {
              name: pending?.pkg?.name ?? '',
              credits: formatCredits(pending?.pkg?.credits)
            })}
          </Alert>
          {pending?.contactEmail && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {pending.contactName ? `${pending.contactName} · ` : ''}
              <MuiLink href={`mailto:${pending.contactEmail}`} underline="hover">
                {pending.contactEmail}
              </MuiLink>
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button variant="contained" onClick={() => setPending(null)} sx={{ textTransform: 'none', boxShadow: 'none' }}>
            {t('common.close', 'Cerrar')}
          </Button>
        </DialogActions>
      </Dialog>
    </CustomerLayout>
  );
};

export default CustomerPlansPage;
